import { useQuery, useMutation, useQueryClient, keepPreviousData } from "@tanstack/react-query";
import { productsApi } from "../api/products.js";
import { queryKeys } from "../lib/queryKeys.js";

// Products change infrequently in a CRM → longer stale times
const STALE = { list: 5 * 60_000, detail: 10 * 60_000 };

// ── Read hooks ────────────────────────────────────────────────────────────────

export function useProducts(filters = {}, options = {}) {
    return useQuery({
        queryKey: queryKeys.products.list(filters),
        queryFn: () => productsApi.getAll(filters),
        staleTime: STALE.list,
        placeholderData: keepPreviousData,
        ...options,
    });
}

export function useProduct(id, options = {}) {
    return useQuery({
        queryKey: queryKeys.products.detail(id),
        queryFn: () => productsApi.getById(id),
        staleTime: STALE.detail,
        enabled: !!id,
        ...options,
    });
}

export function useProductPriceHistory(id, options = {}) {
    return useQuery({
        queryKey: queryKeys.products.priceHistory(id),
        queryFn: () => productsApi.getPriceHistory(id),
        staleTime: STALE.detail,
        enabled: !!id,
        ...options,
    });
}

// ── Mutation hooks ────────────────────────────────────────────────────────────

export function useCreateProduct() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: productsApi.create,
        onSuccess: () => {
            qc.invalidateQueries({ queryKey: queryKeys.products.lists() });
        },
    });
}

export function useUpdateProduct() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: ({ id, payload }) => productsApi.update(id, payload),
        onSuccess: (_, { id }) => {
            qc.invalidateQueries({ queryKey: queryKeys.products.detail(id) });
            qc.invalidateQueries({ queryKey: queryKeys.products.lists() });
        },
    });
}

export function useDeleteProduct() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: productsApi.remove,
        onSuccess: (_, id) => {
            qc.removeQueries({ queryKey: queryKeys.products.detail(id) });
            qc.invalidateQueries({ queryKey: queryKeys.products.lists() });
        },
    });
}

export function useUpdateProductPrice() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: ({ id, payload }) => productsApi.updatePrice(id, payload),
        onSuccess: (_, { id }) => {
            // Price change: refresh price history + invalidate lists (price column)
            qc.invalidateQueries({ queryKey: queryKeys.products.priceHistory(id) });
            qc.invalidateQueries({ queryKey: queryKeys.products.detail(id) });
            qc.invalidateQueries({ queryKey: queryKeys.products.lists() });
        },
    });
}

export function useImportProducts() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: productsApi.importProducts,
        onSuccess: () => {
            // Bulk import → invalidate everything product-related
            qc.invalidateQueries({ queryKey: queryKeys.products.all() });
        },
    });
}
